import { useEffect, useRef } from "react";
import toast from "react-hot-toast";

/**
 * useServiceWorkerUpdate
 *
 * Detects when a new service worker (i.e. a new app build) has been installed
 * and is waiting to take over, then shows a non-intrusive "update available"
 * toast with a Reload button.
 *
 * Flow:
 *   1. A new sw.js is fetched (on load, on tab focus, or via periodic update()).
 *   2. It installs and — because the SW no longer auto-skipWaiting — sits in the
 *      "waiting" state while the current version keeps running.
 *   3. We prompt the user. On "Reload" we post SKIP_WAITING to the waiting
 *      worker; once it takes control (`controllerchange`) we reload the page so
 *      the freshly-built assets load.
 *
 * The first-ever install (no existing controller) never prompts, and the
 * controllerchange auto-reload is gated behind an explicit user accept so a
 * first load can't trigger a reload loop.
 */
export function useServiceWorkerUpdate() {
  const acceptedRef = useRef(false);
  const reloadingRef = useRef(false);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let registration;
    let intervalId;

    // Reload only when the new worker takes control AFTER the user accepted.
    const onControllerChange = () => {
      if (!acceptedRef.current || reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    const promptUpdate = (waitingWorker) => {
      if (promptedRef.current) return;
      promptedRef.current = true;

      toast(
        (t) => (
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-text-primary">
              A new version is available
            </span>
            <button
              onClick={() => {
                acceptedRef.current = true;
                toast.dismiss(t.id);
                if (waitingWorker) {
                  waitingWorker.postMessage({ type: "SKIP_WAITING" });
                } else {
                  reloadingRef.current = true;
                  window.location.reload();
                }
              }}
              className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              Reload
            </button>
          </div>
        ),
        { id: "sw-update", duration: Infinity },
      );
    };

    const trackInstalling = (worker) => {
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        // "installed" + an existing controller means this is an update,
        // not a first-time install.
        if (
          worker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          promptUpdate(registration?.waiting || worker);
        }
      });
    };

    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        registration = reg;

        // A worker was already waiting before this page loaded.
        if (reg.waiting && navigator.serviceWorker.controller) {
          promptUpdate(reg.waiting);
        }

        reg.addEventListener("updatefound", () =>
          trackInstalling(reg.installing),
        );

        // Proactively check for a new build: now, on tab focus, and hourly.
        const checkForUpdate = () => reg.update().catch(() => {});
        checkForUpdate();
        intervalId = setInterval(checkForUpdate, 60 * 60 * 1000);

        const onVisible = () => {
          if (document.visibilityState === "visible") checkForUpdate();
        };
        document.addEventListener("visibilitychange", onVisible);
        registration._onVisible = onVisible;
      })
      .catch(() => {});

    return () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
      if (intervalId) clearInterval(intervalId);
      if (registration?._onVisible) {
        document.removeEventListener("visibilitychange", registration._onVisible);
      }
    };
  }, []);
}
